import { isPlatformBrowser, NgOptimizedImage } from "@angular/common";
import type { OnInit } from "@angular/core";
import { Component, computed, DestroyRef, ElementRef, HostListener, inject, PLATFORM_ID, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import type { LangDefinition } from "@jsverse/transloco";
import { TranslocoPipe, TranslocoService } from "@jsverse/transloco";
import { SvgIconComponent } from "angular-svg-icon";

import { availableLanguages } from "../../../common/i18n/transloco/available-languages";
import { DirectusTranslationPipe } from "../../../common/pipes/directus-translation.pipe";
import { GetDirectusImagePipe } from "../../../common/pipes/get-directus-image.pipe";
import { PathFromItemPipe } from "../../../common/pipes/path-from-item.pipe";
import { sanitizeDirectusObject, sanitizeDirectusRelationArray } from "../../../common/utils/sanitize-directus-object";
import type { ItemsNavigationItems } from "../../api/packbo/models/items-navigation-items";
import { NavigationService } from "./store/navigation.service";
import { NavigationStore } from "./store/navigation.store";

@Component({
    selector: "packbo-navigation",
    templateUrl: "navigation.template.html",
    styleUrls: ["navigation.style.css"],
    standalone: true,
    providers: [NavigationService, NavigationStore],
    imports: [
        TranslocoPipe,
        SvgIconComponent,
        DirectusTranslationPipe,
        GetDirectusImagePipe,
        PathFromItemPipe,
        NgOptimizedImage
    ]
})
export class NavigationComponent implements OnInit {
    private readonly destroyRef = inject(DestroyRef);
    private readonly translocoService = inject(TranslocoService);
    private readonly elementRef = inject(ElementRef);
    private readonly platformId = inject(PLATFORM_ID);
    private readonly navigationService = inject(NavigationService);
    private readonly navigationStore = inject(NavigationStore);

    public categories = this.navigationStore.categories;
    public navbarDefinition = this.navigationStore.navbarDefinition;
    public navigationItems = computed(
        () =>
            sanitizeDirectusRelationArray(
                this.navbarDefinition()?.navigation_items_refs,
                "navigation_items_id"
            ) as ItemsNavigationItems[]
    );
    public loginNavigationItem = computed(() =>
        sanitizeDirectusObject(this.navbarDefinition()?.login_navigation_item_ref)
    );
    public membershipNavigationItem = computed(() =>
        sanitizeDirectusObject(this.navbarDefinition()?.membership_navigation_item_ref)
    );

    public isScrolled = false;
    public isMenuOpen = false;

    public alternativeLanguageDefinition: LangDefinition = availableLanguages.find(
        (lang) => lang.id !== this.translocoService.getActiveLang()
    ) as LangDefinition;

    protected homeUrl = signal<string>(`/${this.translocoService.getActiveLang()}`);

    constructor() {
        this.translocoService.langChanges$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((newLang) => {
            this.alternativeLanguageDefinition = availableLanguages.find(
                (lang) => lang.id !== newLang
            ) as LangDefinition;
            this.homeUrl.set(`/${newLang}`);
        });
    }

    public ngOnInit(): void {
        this.navigationService.loadNavbarContent().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        if (isPlatformBrowser(this.platformId)) {
            this.checkScrollPosition();
        }
    }

    @HostListener("window:scroll", [])
    public onWindowScroll(): void {
        if (isPlatformBrowser(this.platformId)) {
            this.checkScrollPosition();
        }
    }

    @HostListener("document:click", ["$event"])
    public onDocumentClick(event: MouseEvent): void {
        if (
            isPlatformBrowser(this.platformId) &&
            this.isMenuOpen &&
            event.target &&
            !(this.elementRef.nativeElement as Element).contains(event.target as Node)
        ) {
            this.isMenuOpen = false;
        }
    }

    private checkScrollPosition(): void {
        if (isPlatformBrowser(this.platformId)) {
            this.isScrolled = window.pageYOffset > 0;
        }
    }

    public toggleMenu(): void {
        this.isMenuOpen = !this.isMenuOpen;
    }

    public switchLanguage(): void {
        const newLanguage = this.alternativeLanguageDefinition.id;
        if (newLanguage) {
            this.translocoService.setActiveLang(newLanguage);
        }
    }
}
